import { Tabs, Button, Icon, Input, DatePicker } from 'ui';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import DefaultLayout from 'admin/components/DefaultLayout';
import Listings, { ListingEditOptions } from '../../../components/Listings';
import { useAuth } from 'hooks/useAuth';
import { permissions } from 'config/config';
import { StyledButtonList } from 'styles/buttonList';
import { StyledMobileHeaderWrapper } from 'admin/components/MobileLayout/style';
import { DrawerCreateUpdateListing } from 'ui/Drawer/Drawers/DrawerCreateUpdateListing';
import { DrawerCreateUpdateListingApplication } from 'ui/Drawer/Drawers/DrawerCreateUpdateListingApplication';
import { DrawerViewListingApplication } from 'ui/Drawer/Drawers/DrawerViewListingApplication';
import { useState, useEffect, useMemo } from 'react';
import ListingsApplications from 'admin/components/Listings/ListingsApplications';
import { message } from 'antd';
import useQueryApiClient from 'utils/useQueryApiClient';
import Filter from 'admin/components/Filter';
import EnumSelect from 'admin/Selects/EnumSelect';
import AdminMobileFilterDrawer from 'ui/Drawer/Drawers/AdminMobileFilterDrawer';
import type { ListingFilterState } from 'types/ListingData';
import dayjs from 'dayjs';
import { useBreakpoint } from 'utils/useBreakpoint';

const EDITABLE_APPLICATION_STATUSES = ['new', 'rejected'];

const CANNOT_APPLY_REASON_MESSAGES: Record<string, string> = {
  wholesale_contract_required:
    'message.wholesale_contract_required_for_listings',
  dynamic_contract_required: 'message.dynamic_contract_required_for_listings',
};

export const ListingsPage = () => {
  const intl = useIntl();
  const { isLgUp } = useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const [editingListingId, setEditingListingId] = useState<string | null>(null);
  const [startOnFirstTab, setStartOnFirstTab] = useState(false);
  const [applicationDrawerOpen, setApplicationDrawerOpen] = useState(false);
  const [targetListingId, setTargetListingId] = useState<string | null>(null);
  const [editingApplicationId, setEditingApplicationId] = useState<
    number | null
  >(null);
  const [viewApplicationDrawerOpen, setViewApplicationDrawerOpen] =
    useState(false);
  const [applicationToViewId, setApplicationToViewId] = useState<number | null>(
    null
  );
  const [viewedApplication, setViewedApplication] = useState<{
    id: number;
    listing_id: string;
  } | null>(null);
  const { user, can } = useAuth();
  const canEditListing = can(permissions.edit_farm_listing);
  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get('listingId') ? 'my_applications' : 'my_listings'
  );
  const [pendingListingId, setPendingListingId] = useState<string | null>(null);
  const [isListingsFilterOpen, setIsListingsFilterOpen] = useState(false);
  const [isApplicationsFilterOpen, setIsApplicationsFilterOpen] =
    useState(false);
  const [listingsFilter, setListingsFilter] = useState<ListingFilterState>({});
  const [applicationsFilter, setApplicationsFilter] = useState<
    Record<string, unknown>
  >({ page_size: 10 });
  const [listingsPaginationStatus, setListingsPaginationStatus] = useState<{
    total?: number;
  }>({});
  const [applicationsPaginationStatus, setApplicationsPaginationStatus] =
    useState<{ total?: number }>({});

  const [checkingApplications, setCheckingApplications] = useState(false);

  const openFilter = () => {
    if (activeTab === 'my_listings') {
      setIsListingsFilterOpen(true);
    } else {
      setIsApplicationsFilterOpen(true);
    }
  };

  const closeFilters = () => {
    setIsListingsFilterOpen(false);
    setIsApplicationsFilterOpen(false);
  };

  const [listingsReloadFilter, setListingsReloadFilter] = useState(0);
  const [applicationsReloadFilter, setApplicationsReloadFilter] = useState(0);

  const resetListingsFilter = () => {
    setListingsFilter({});
    setListingsReloadFilter((prev) => prev + 1);
  };

  const resetApplicationsFilter = () => {
    setApplicationsFilter({ page_size: 10 });
    setApplicationsReloadFilter((prev) => prev + 1);
  };

  useQueryApiClient({
    request: {
      url: `/api/v2/listings/${pendingListingId}`,
    },
    enabled:
      !!pendingListingId && !applicationDrawerOpen && !checkingApplications,
    onSuccess: (data) => {
      if (data?.farm_id === user?.farmId) {
        message.error(
          intl.formatMessage({ id: 'message.cannot_apply_to_own_listing' })
        );
        setPendingListingId(null);
        if (searchParams.get('listingId')) {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('listingId');
          setSearchParams(newSearchParams);
        }
      } else {
        setCheckingApplications(true);
      }
    },
    onError: () => {
      setPendingListingId(null);
      setCheckingApplications(false);
      if (searchParams.get('listingId')) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('listingId');
        setSearchParams(newSearchParams);
      }
    },
  });

  useQueryApiClient({
    request: {
      url: `/api/v2/listings/${pendingListingId}/can-apply`,
    },
    enabled:
      !!pendingListingId && checkingApplications && !applicationDrawerOpen,
    onSuccess: (data) => {
      if (!pendingListingId) {
        setCheckingApplications(false);
        return;
      }

      setCheckingApplications(false);

      if (!data?.can_apply) {
        message.error(
          intl.formatMessage({
            id:
              CANNOT_APPLY_REASON_MESSAGES[data?.reason] ??
              'message.cannot_apply_existing_application',
          })
        );
        setPendingListingId(null);
        if (searchParams.get('listingId')) {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('listingId');
          setSearchParams(newSearchParams);
        }
      } else {
        setTargetListingId(pendingListingId);
        setApplicationDrawerOpen(true);
        setPendingListingId(null);
      }
    },
    onError: () => {
      setCheckingApplications(false);
      setTargetListingId(pendingListingId);
      setApplicationDrawerOpen(true);
      setPendingListingId(null);
    },
  });

  useQueryApiClient({
    request: {
      url: `/api/v2/listing-applications/${applicationToViewId}`,
    },
    enabled: !!applicationToViewId,
    onSuccess: (data: {
      id: number;
      listing_id: string;
      applicant_farm_id?: number;
      status?: string;
    }) => {
      const isOwner = user?.farmId === data.applicant_farm_id;
      const isEditableStatus =
        data.status && EDITABLE_APPLICATION_STATUSES.includes(data.status);
      if (isOwner && isEditableStatus) {
        setTargetListingId(data.listing_id);
        setEditingApplicationId(data.id);
        setApplicationDrawerOpen(true);
      } else {
        setViewedApplication({ id: data.id, listing_id: data.listing_id });
        setViewApplicationDrawerOpen(true);
      }
      setApplicationToViewId(null);
    },
    onError: () => {
      setApplicationToViewId(null);
    },
  });

  useEffect(() => {
    const viewListingIdParam = searchParams.get('viewListingId');
    const applicationIdParam = searchParams.get('applicationId');
    const listingIdParam = searchParams.get('listingId');

    if (viewListingIdParam && !drawerOpen && !editingListingId) {
      setEditingListingId(viewListingIdParam);
      setDrawerOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('viewListingId');
      setSearchParams(newSearchParams);
    } else if (
      applicationIdParam &&
      !applicationDrawerOpen &&
      !editingApplicationId &&
      !viewApplicationDrawerOpen &&
      !applicationToViewId &&
      !viewedApplication
    ) {
      const applicationId = parseInt(applicationIdParam);
      setApplicationToViewId(applicationId);
    } else if (
      listingIdParam &&
      !pendingListingId &&
      !applicationDrawerOpen &&
      !editingApplicationId
    ) {
      setPendingListingId(listingIdParam);
    }
  }, [
    searchParams,
    drawerOpen,
    editingListingId,
    pendingListingId,
    applicationDrawerOpen,
    editingApplicationId,
    viewApplicationDrawerOpen,
    applicationToViewId,
    viewedApplication,
  ]);

  const handleCreate = () => {
    setEditingListingId(null);
    setStartOnFirstTab(false);
    setDrawerOpen(true);
  };

  const handleEdit = (listingId: string, options?: ListingEditOptions) => {
    setEditingListingId(listingId);
    setStartOnFirstTab(!!options?.startOnFirstTab);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingListingId(null);
    setStartOnFirstTab(false);
  };

  const handleApplicationEdit = (applicationId: number) => {
    setEditingApplicationId(applicationId);
    setApplicationDrawerOpen(true);
  };

  const handleApplicationDrawerClose = () => {
    setApplicationDrawerOpen(false);
    setTargetListingId(null);
    setEditingApplicationId(null);
    setPendingListingId(null);
    if (searchParams.get('listingId') || searchParams.get('applicationId')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('listingId');
      newSearchParams.delete('applicationId');
      setSearchParams(newSearchParams);
    }
  };

  const handleViewApplicationDrawerClose = () => {
    setViewApplicationDrawerOpen(false);
    setViewedApplication(null);
    if (searchParams.get('applicationId')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('applicationId');
      setSearchParams(newSearchParams);
    }
  };

  const listingsFilterContent = (
    <Filter
      onFinish={(values) => {
        setListingsFilter(values as ListingFilterState);
        closeFilters();
      }}
      totalRecord={listingsPaginationStatus?.total ?? 0}
      isLabelVisible={true}
      defaultFilterParam={listingsFilter}
      reloadFilterTag={listingsReloadFilter}
    >
      <Input
        label={intl.formatMessage({ id: 'listings.search' })}
        name="search"
        placeholder={intl.formatMessage({
          id: 'listings.search_placeholder',
        })}
        allowClear
      />
      <EnumSelect
        label={intl.formatMessage({ id: 'listings.filter_by_status' })}
        name="status"
        mode="multiple"
        code="ListingStatus"
      />
      <EnumSelect
        label={intl.formatMessage({ id: 'listings.filter_by_type' })}
        name="transaction_type"
        mode="multiple"
        code="ListingTransactionType"
      />
      <DatePicker
        label={intl.formatMessage({
          id: 'listings.filter_by_publication_end',
        })}
        name="publication_date_to"
      />
      <div className="button-section-container">
        <div className="button-section-wrapper">
          <Button
            className="mobile-button"
            type="link"
            label={intl.formatMessage({ id: 'general.delete_filter' })}
            onClick={resetListingsFilter}
          />
          <Button
            className="mobile-button"
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={closeFilters}
          />
          <Button
            type="primary"
            label={
              isLgUp
                ? intl.formatMessage({ id: 'general.select' })
                : intl.formatMessage({ id: 'general.filter_action' })
            }
            htmlType="submit"
          />
        </div>
      </div>
    </Filter>
  );

  const applicationsFilterContent = (
    <Filter
      onFinish={(values) => {
        setApplicationsFilter(values as Record<string, unknown>);
        closeFilters();
      }}
      totalRecord={applicationsPaginationStatus?.total ?? 0}
      isLabelVisible={true}
      defaultFilterParam={applicationsFilter}
      reloadFilterTag={applicationsReloadFilter}
    >
      <Input
        label={intl.formatMessage({ id: 'listings.applications.search' })}
        name="search"
        allowClear
      />
      <EnumSelect
        label={intl.formatMessage({
          id: 'listings.filter_by_status',
        })}
        name="status"
        mode="multiple"
        code="ListingApplicationStatus"
      />
      <EnumSelect
        label={intl.formatMessage({
          id: 'listings.filter_by_type',
        })}
        name="transaction_type"
        mode="multiple"
        code="ListingTransactionType"
      />
      <DatePicker
        label={intl.formatMessage({
          id: 'listings.filter_by_submitted_until',
        })}
        name="submitted_at"
        disabledDate={(current) => {
          if (!current) return false;
          return current.isBefore(dayjs().startOf('day'), 'day');
        }}
      />
      <div className="button-section-container">
        <div className="button-section-wrapper">
          <Button
            className="mobile-button"
            type="link"
            label={intl.formatMessage({ id: 'general.delete_filter' })}
            onClick={resetApplicationsFilter}
          />
          <Button
            className="mobile-button"
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={closeFilters}
          />
          <Button
            type="primary"
            label={
              isLgUp
                ? intl.formatMessage({ id: 'general.select' })
                : intl.formatMessage({ id: 'general.filter_action' })
            }
            htmlType="submit"
          />
        </div>
      </div>
    </Filter>
  );

  const items = useMemo(
    () => [
      {
        key: 'my_listings',
        label: intl.formatMessage({ id: 'general.my_listings' }),
        children: (
          <Listings
            reload={reload}
            onEdit={handleEdit}
            filter={listingsFilter}
            onFilterChange={setListingsFilter}
            onPaginationChange={setListingsPaginationStatus}
            filterContent={listingsFilterContent}
          />
        ),
      },
      {
        key: 'my_applications',
        label: intl.formatMessage({ id: 'general.my_applications' }),
        children: (
          <ListingsApplications
            reload={reload}
            onEdit={handleApplicationEdit}
            filter={applicationsFilter}
            onFilterChange={setApplicationsFilter}
            onPaginationChange={setApplicationsPaginationStatus}
            filterContent={applicationsFilterContent}
          />
        ),
      },
    ],
    [intl, reload, listingsFilter, applicationsFilter]
  );

  return (
    <>
      <StyledMobileHeaderWrapper>
        {canEditListing && (
          <Button
            type="text"
            label={`+ ${intl.formatMessage({ id: 'general.add_listing' })}`}
            onClick={handleCreate}
          />
        )}
        <div className="mobile-header-item-wrapper">
          <Button
            type="text"
            label={intl.formatMessage({ id: 'general.filter' })}
            onClick={openFilter}
            icon={
              <Icon
                name="admin-filter-icon"
                className="admin-header-item-icon"
              />
            }
          />
        </div>
      </StyledMobileHeaderWrapper>
      <DefaultLayout.PageLayout
        breadcrumbs={[
          {
            title: intl.formatMessage({ id: 'navigation.beginning' }),
            href: '/',
          },
          {
            title: intl.formatMessage({ id: 'navigation.listings' }),
          },
        ]}
      >
        <DefaultLayout.PageContent>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              closeFilters();
            }}
            tabBarExtraContent={
              canEditListing ? (
                <StyledButtonList className="hide-mobile">
                  <Button
                    type="primary"
                    label={intl.formatMessage({ id: 'general.add_listing' })}
                    onClick={handleCreate}
                  />
                </StyledButtonList>
              ) : null
            }
            items={items}
          />
          <AdminMobileFilterDrawer
            isOpen={isListingsFilterOpen}
            onClose={closeFilters}
          >
            {listingsFilterContent}
          </AdminMobileFilterDrawer>
          <AdminMobileFilterDrawer
            isOpen={isApplicationsFilterOpen}
            onClose={closeFilters}
          >
            {applicationsFilterContent}
          </AdminMobileFilterDrawer>
        </DefaultLayout.PageContent>

        <DrawerCreateUpdateListing
          open={drawerOpen}
          onClose={handleDrawerClose}
          closeDrawer={setDrawerOpen}
          setReload={setReload}
          listingId={editingListingId}
          viewOnly={!canEditListing}
          startOnFirstTab={startOnFirstTab}
        />

        <DrawerCreateUpdateListingApplication
          open={applicationDrawerOpen}
          onClose={handleApplicationDrawerClose}
          listingId={targetListingId}
          applicationId={editingApplicationId}
          setReload={setReload}
        />

        <DrawerViewListingApplication
          open={viewApplicationDrawerOpen}
          onClose={handleViewApplicationDrawerClose}
          application={viewedApplication}
          onActionCompleted={() => setReload((r) => r + 1)}
        />
      </DefaultLayout.PageLayout>
    </>
  );
};
